/**
 * Shared consumer transcript renderer.
 *
 * AGENT REPLIES MUST BE VISUALLY DISTINCT FROM AI REPLIES — a customer has to
 * be able to tell whether a person has answered. Agent turns get a filled
 * accent card and a person label; assistant turns get a plain outlined card
 * labelled as the assistant.
 */
import { User, Bot, Headset } from "lucide-react";
import { cn } from "@/lib/utils";
import { AUTHOR_LABEL, type SupportAuthor, type ThreadMessage } from "@/services/support";

function when(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ICON: Record<SupportAuthor, typeof User> = {
  requester: User,
  agent: Headset,
  ai: Bot,
};

export function TranscriptMessage({ message }: { message: ThreadMessage }) {
  const kind = message.author_kind;
  const Icon = ICON[kind] ?? Bot;
  const isMine = kind === "requester";

  return (
    <div className={cn("flex w-full", isMine ? "justify-end" : "justify-start")}>
      <div className={cn("max-w-[85%] space-y-1", isMine && "items-end text-right")}>
        <div
          className={cn(
            "flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide",
            isMine && "justify-end",
            kind === "agent" ? "text-primary" : "text-muted-foreground",
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
          <span>{AUTHOR_LABEL[kind] ?? "Support"}</span>
          {kind === "agent" && (
            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
              Real person
            </span>
          )}
        </div>
        <div
          className={cn(
            "whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-left text-sm leading-relaxed",
            isMine && "bg-primary text-primary-foreground",
            kind === "agent" && "border-2 border-primary bg-primary/10 text-foreground",
            kind === "ai" && "border border-border bg-muted/40 text-foreground",
          )}
        >
          {message.body}
        </div>
        <p className="text-[11px] text-muted-foreground">{when(message.created_at)}</p>
      </div>
    </div>
  );
}

export default function ThreadTranscript({ messages }: { messages: ThreadMessage[] }) {
  if (messages.length === 0) {
    return <p className="text-sm text-muted-foreground">No messages yet.</p>;
  }
  return (
    <div className="space-y-4">
      {messages.map((m) => (
        <TranscriptMessage key={m.id} message={m} />
      ))}
    </div>
  );
}
